import { RefObject, useCallback, useEffect, useRef, useState } from 'react'

import { IScrollButton } from 'core-components/library/list/table/models/IRecalculatingScroll'

interface IUseRecalculatingScroll {
  containerRef: RefObject<HTMLTableElement | null>
  scrollRef: RefObject<HTMLDivElement | null>
  recalculateData: unknown[]
}

export const useRecalculatingScroll = ({
  containerRef,
  scrollRef,
  recalculateData,
}: IUseRecalculatingScroll) => {
  const [hasHorizontalScroll, setHasHorizontalScroll] = useState<boolean>(false)
  const [hasVerticalScroll, setHasVerticalScroll] = useState<boolean>(false)
  const [scrollButtons, setScrollButtons] = useState<IScrollButton>({
    next: 'hidden',
    previous: 'hidden',
  })

  const throttle = 50
  const throttleRef = useRef<number>(0)

  const calcScrollButtonVisible = useCallback(
    (newScrollPosition: number) => {
      if (throttleRef.current) {
        window.clearTimeout(throttleRef.current)
      }

      throttleRef.current = window.setTimeout(() => {
        throttleRef.current = 0

        const scroll = scrollRef.current

        if (scrollRef.current) {
          const newEnableNavigationValue =
            scrollRef.current.scrollWidth - scrollRef.current.clientWidth > 10
          if (!newEnableNavigationValue) {
            setScrollButtons((prev) => {
              if (prev.next !== 'hidden' || prev.previous !== 'hidden') {
                return { next: 'hidden', previous: 'hidden' }
              }
              return prev
            })
          } else {
            const MIN_SCROLL_STEP = 1
            const isPrevButtonDisabled = scroll ? newScrollPosition <= MIN_SCROLL_STEP : false
            const isNextButtonDisabled = scroll
              ? scroll.scrollWidth - (newScrollPosition + scroll.clientWidth) < MIN_SCROLL_STEP
              : false

            setScrollButtons((prev) => {
              const newValue: IScrollButton = {
                next: isNextButtonDisabled ? 'disabled' : 'visible',
                previous: isPrevButtonDisabled ? 'disabled' : 'visible',
              }
              if (newValue.next !== prev.next || newValue.previous !== prev.previous) {
                return newValue
              }
              return prev
            })
          }
        }
      }, throttle)
    },
    [scrollRef]
  )

  useEffect(() => {
    if (scrollRef.current) {
      const newEnableNavigationValue =
        scrollRef.current.scrollWidth - scrollRef.current.clientWidth > 10
      const isNavigationEnabled =
        scrollButtons.next !== 'hidden' || scrollButtons.previous !== 'hidden'

      if (newEnableNavigationValue !== isNavigationEnabled) {
        calcScrollButtonVisible(0)
      }
    }
  })

  useEffect(() => {
    setTimeout(() => {
      if (scrollRef.current) {
        calcScrollButtonVisible(scrollRef.current.scrollLeft)
      }
    }, 0)
  }, [calcScrollButtonVisible, hasHorizontalScroll, scrollRef])

  useEffect(() => {
    if (typeof window.ResizeObserver === 'undefined') {
      return () => undefined
    }

    const resizeObserver = new ResizeObserver(() => {
      setTimeout(() => {
        const { current } = containerRef
        if (current) {
          const parent = current.parentElement as HTMLElement
          const horizontalDif = current.clientWidth - parent.clientWidth
          const verticalDif = current.clientHeight - parent.clientHeight
          setHasHorizontalScroll(horizontalDif > 0)
          setHasVerticalScroll(verticalDif > 0)
          if (scrollRef.current) {
            calcScrollButtonVisible(scrollRef.current.scrollLeft)
          }
        }
      }, 0)
    })

    if (containerRef.current) {
      resizeObserver.observe(containerRef.current)
    }
    return () => {
      if (containerRef.current) {
        return resizeObserver.unobserve(containerRef.current)
      }
      return undefined
    }
  }, [scrollRef.current, containerRef.current, calcScrollButtonVisible, ...recalculateData])

  return {
    hasHorizontalScroll,
    scrollButtons,
    calcScrollButtonVisible,
    hasVerticalScroll,
  }
}
